import { memo, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { TeamStrategy } from "@/stores/WorkflowStore";

// Text truncation length constants
const TRUNCATE_LENGTH_SHORT = 40;
const TRUNCATE_LENGTH_MEDIUM = 80;
const TRUNCATE_LENGTH_LONG = 120;

interface MemberData {
  name: string;
  type: string;
  parameters?: Record<string, any>;
  tools?: string[];
  // Sub-team specific fields
  isSubTeam?: boolean;
  strategy?: string;
  goal?: string;
  memberCount?: number;
  // Error state for missing members
  notFound?: boolean;
}

interface TeamNodeProps {
  data: {
    name: string;
    members: string[];
    memberData?: Record<string, MemberData>;
    goal?: string;
    maxCycles?: number;
    strategy?: TeamStrategy;
    model?: string;
    emoji: React.ReactNode;
  };
}

function TeamNode({ data }: TeamNodeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = () => setExpanded((prev) => !prev);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggleExpand();
    }
  };

  const formatName = (name: string) =>
    name
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: string) => char.toUpperCase());

  // Truncate long text with ellipsis
  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text || text.length <= maxLength) return text;

    return text.substring(0, maxLength) + "...";
  };

  // Check if we have detailed member data
  const hasMemberData = data.memberData && Object.keys(data.memberData).length > 0;

  // Determine strategy display
  const strategy = data.strategy || "selector";
  const isSelector = strategy === "selector";
  const isMixture = strategy === "mixture";

  // Strategy-specific styling - consistent with AgentNode style
  const strategyConfig = {
    selector: {
      borderColor: "border-blue-400 dark:border-blue-600",
      bgColor: "bg-white dark:bg-default-100",
      handleColor: "!bg-blue-500",
      badgeBg: "bg-blue-100 dark:bg-blue-900",
      badgeText: "text-blue-700 dark:text-blue-300",
      icon: "mdi:arrow-decision",
      label: "Selector",
    },
    mixture: {
      borderColor: "border-purple-400 dark:border-purple-600",
      bgColor: "bg-white dark:bg-default-100",
      handleColor: "!bg-purple-500",
      badgeBg: "bg-purple-100 dark:bg-purple-900",
      badgeText: "text-purple-700 dark:text-purple-300",
      icon: "mdi:source-merge",
      label: "Mixture",
    },
  };

  const config = strategyConfig[strategy] || strategyConfig.selector;

  // Compact view (similar to AgentNode)
  if (!expanded) {
    return (
      <div
        className={`px-4 py-2 shadow-md rounded-md ${config.bgColor} border-2 ${config.borderColor} w-100 min-h-40 cursor-pointer`}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(true)}
        onKeyDown={handleKeyDown}
      >
        <div className="flex">
          <div className="ml-2 w-full">
            {/* Header - same style as AgentNode */}
            <div className="text-lg font-light text-primary flex items-center gap-2">
              {data.emoji}
              {formatName(data.name)}
            </div>

            {/* Type with strategy badge */}
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <span>Team</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${config.badgeBg} ${config.badgeText} flex items-center gap-1`}
              >
                <Icon className="text-xs" icon={config.icon} />
                {config.label}
              </span>
            </div>

            {/* Key parameters - same style as AgentNode */}
            <div className="max-h-22 overflow-y-auto w-full text-foreground mt-1">
              <div className="text-sm">
                <b>members</b> : {data.members.length}
              </div>
              {data.maxCycles && (
                <div className="text-sm">
                  <b>maxCycles</b> : {data.maxCycles}
                </div>
              )}
              {data.model && (
                <div className="text-sm">
                  <b>model</b> : {data.model}
                </div>
              )}
              {data.goal && (
                <div className="text-sm">
                  <b>goal</b> : {truncateText(data.goal, TRUNCATE_LENGTH_SHORT)}
                </div>
              )}
            </div>

            {/* Expand hint */}
            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <Icon icon="mdi:chevron-down" />
              <span>Click to expand</span>
            </div>
          </div>
        </div>

        <Handle className={`w-16 ${config.handleColor}`} position={Position.Top} type="target" />
        <Handle className={`w-16 ${config.handleColor}`} position={Position.Bottom} type="source" />
      </div>
    );
  }

  // Expanded view with member details
  return (
    <div
      className={`shadow-md rounded-lg ${config.bgColor} border-2 ${config.borderColor} min-w-[320px]`}
    >
      {/* Team Header - Compact */}
      <div
        className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(false)}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-2">
          {data.emoji}
          <div className="flex-1">
            <div className="text-lg font-light text-primary">{formatName(data.name)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <span>Team</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${config.badgeBg} ${config.badgeText} flex items-center gap-1`}
              >
                <Icon className="text-xs" icon={config.icon} />
                {config.label}
              </span>
            </div>
          </div>
          <Icon className="text-gray-400" icon="mdi:chevron-up" />
        </div>

        {/* Key info row */}
        <div className="flex gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
          {data.maxCycles && (
            <span className="flex items-center gap-1">
              <Icon icon="mdi:repeat" />
              {data.maxCycles} cycles
            </span>
          )}
          {data.model && (
            <span className="flex items-center gap-1">
              <Icon icon="mdi:chip" />
              {data.model}
            </span>
          )}
        </div>

        {data.goal && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic" title={data.goal}>
            {truncateText(data.goal, TRUNCATE_LENGTH_MEDIUM)}
          </div>
        )}

        {/* Strategy Explanation */}
        <div
          className={`mt-3 p-2 rounded-md ${isSelector ? "bg-blue-50 dark:bg-blue-900/30" : "bg-purple-50 dark:bg-purple-900/30"}`}
        >
          {isSelector ? (
            <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
              <Icon className="text-base" icon="mdi:account-supervisor" />
              <Icon className="text-xs" icon="mdi:arrow-right" />
              <div className="flex items-center gap-0.5">
                {data.members.slice(0, 3).map((_, idx) => (
                  <span key={idx} className="flex items-center">
                    <Icon className="text-sm" icon="mdi:account" />
                    {idx < Math.min(data.members.length - 1, 2) && (
                      <Icon className="text-xs opacity-50" icon="mdi:chevron-double-right" />
                    )}
                  </span>
                ))}
                {data.members.length > 3 && <span className="text-[10px]">...</span>}
              </div>
              <Icon className="text-xs" icon="mdi:arrow-right" />
              <Icon className="text-base" icon="mdi:check-circle" />
              <span className="ml-1 text-[10px] opacity-75">
                {t("Sequential - Manager selects next speaker")}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
              <Icon className="text-base" icon="mdi:account-supervisor" />
              <div className="flex flex-col items-center -my-1">
                {[0, 1, 2].map((i) => (
                  <Icon key={i} className="text-[8px]" icon="mdi:arrow-right-bold" />
                ))}
              </div>
              <div className="flex flex-col items-center gap-0 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-800 rounded">
                {data.members.slice(0, 3).map((_, idx) => (
                  <Icon key={idx} className="text-sm -my-0.5" icon="mdi:account" />
                ))}
                {data.members.length > 3 && (
                  <span className="text-[8px]">+{data.members.length - 3}</span>
                )}
              </div>
              <div className="flex flex-col items-center -my-1">
                {[0, 1, 2].map((i) => (
                  <Icon key={i} className="text-[8px]" icon="mdi:arrow-right-bold" />
                ))}
              </div>
              <Icon className="text-base" icon="mdi:merge" />
              <span className="ml-1 text-[10px] opacity-75">
                {t("Parallel - Manager aggregates all")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Team Members Container */}
      <div className="p-3 max-h-[350px] overflow-y-auto">
        {hasMemberData ? (
          // Render detailed member nodes with connection lines
          <div className="relative">
            {/* Vertical connection line for selector (sequential) */}
            {isSelector && data.members.length > 1 && (
              <div
                className="absolute left-[9px] top-4 bottom-4 w-0.5 bg-blue-300 dark:bg-blue-600"
                style={{ zIndex: 1 }}
              />
            )}
            {/* Horizontal branch lines for mixture (parallel) */}
            {isMixture && data.members.length > 0 && (
              <div
                className="absolute left-[9px] top-4 bottom-4 w-0.5 bg-purple-300 dark:bg-purple-600"
                style={{ zIndex: 1 }}
              />
            )}

            {data.members.map((memberId, index) => {
              const member = data.memberData?.[memberId];

              if (!member) return null;

              const isLastMember = index === data.members.length - 1;

              // Check if member was not found (error state)
              if (member.notFound) {
                return (
                  <div key={memberId} className="relative">
                    {/* Connection node with horizontal branch */}
                    <div className="absolute left-0 top-4 flex items-center" style={{ zIndex: 5 }}>
                      <div className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                        <Icon className="text-sm" icon="mdi:alert" />
                      </div>
                      {/* Horizontal connector to member card */}
                      <div className="w-3 h-0.5 bg-red-300" />
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-md border-2 border-red-300 dark:border-red-600 p-2 mb-3 last:mb-0 ml-8">
                      <div className="flex items-center gap-2">
                        <Icon className="text-base text-red-500" icon="mdi:account-alert" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-red-700 dark:text-red-300">
                            {formatName(memberId)}
                          </div>
                          <div className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
                            <Icon className="text-xs" icon="mdi:alert-circle" />
                            <span>{t("Member not found in workflow")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // Check if this member is a sub-team
              if (member.isSubTeam) {
                const subStrategy = member.strategy || "selector";
                const isSubSelector = subStrategy === "selector";

                return (
                  <div key={memberId} className="relative">
                    {/* Connection node with horizontal branch */}
                    <div className="absolute left-0 top-4 flex items-center" style={{ zIndex: 5 }}>
                      <div
                        className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold ${
                          isSelector ? "bg-blue-500" : "bg-purple-500"
                        }`}
                      >
                        {isMixture ? <Icon className="text-sm" icon="mdi:equal" /> : index + 1}
                      </div>
                      {/* Horizontal connector to member card */}
                      <div
                        className={`w-3 h-0.5 ${isSelector ? "bg-blue-300" : "bg-purple-300"}`}
                      />
                    </div>
                    {/* Arrow for selector showing flow direction */}
                    {isSelector && !isLastMember && (
                      <div
                        className="absolute left-[6px] bottom-0 text-blue-400"
                        style={{ zIndex: 6 }}
                      >
                        <Icon className="text-xs" icon="mdi:chevron-down" />
                      </div>
                    )}
                    <div
                      className={`rounded-md border-2 border-dashed p-2 mb-3 last:mb-0 ml-8 relative ${
                        isSubSelector
                          ? "border-blue-300 bg-blue-50/50 dark:bg-blue-900/20"
                          : "border-purple-300 bg-purple-50/50 dark:bg-purple-900/20"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="text-base" icon="fluent-color:people-team-48" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{formatName(memberId)}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <span>Sub-team</span>
                            <span className="text-gray-400">•</span>
                            <span className={isSubSelector ? "text-blue-600" : "text-purple-600"}>
                              {isSubSelector ? "Selector" : "Mixture"}
                            </span>
                            {member.memberCount !== undefined && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span>{member.memberCount} members</span>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Dotted line connector to external team node */}
                        <div className="flex items-center gap-1">
                          <div
                            className={`w-8 h-0.5 ${isSubSelector ? "bg-blue-400" : "bg-purple-400"}`}
                            style={{
                              backgroundImage: `repeating-linear-gradient(to right, ${isSubSelector ? "#60a5fa" : "#a78bfa"} 0, ${isSubSelector ? "#60a5fa" : "#a78bfa"} 4px, transparent 4px, transparent 8px)`,
                              backgroundSize: "8px 2px",
                            }}
                          />
                          <Icon
                            className={`text-lg ${isSubSelector ? "text-blue-500" : "text-purple-500"}`}
                            icon="mdi:arrow-right-circle"
                          />
                        </div>
                      </div>
                      {/* Hint text */}
                      <div className="text-[10px] text-gray-400 mt-1 text-right">
                        → connected to team node
                      </div>
                    </div>
                  </div>
                );
              }

              // Regular agent member
              const model = member.parameters?.model;
              const systemMessage = member.parameters?.system_message;

              return (
                <div key={memberId} className="relative">
                  {/* Connection node with horizontal branch */}
                  <div className="absolute left-0 top-4 flex items-center" style={{ zIndex: 5 }}>
                    <div
                      className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold ${
                        isSelector ? "bg-blue-500" : "bg-purple-500"
                      }`}
                    >
                      {isMixture ? <Icon className="text-sm" icon="mdi:equal" /> : index + 1}
                    </div>
                    {/* Horizontal connector to member card */}
                    <div className={`w-3 h-0.5 ${isSelector ? "bg-blue-300" : "bg-purple-300"}`} />
                  </div>
                  {/* Arrow for selector showing flow direction */}
                  {isSelector && !isLastMember && (
                    <div
                      className="absolute left-[6px] bottom-0 text-blue-400"
                      style={{ zIndex: 6 }}
                    >
                      <Icon className="text-xs" icon="mdi:chevron-down" />
                    </div>
                  )}
                  <div
                    className={`bg-gray-50 dark:bg-default-50 rounded-md border p-2 mb-3 last:mb-0 ml-8 ${
                      isSelector
                        ? "border-blue-200"
                        : isMixture
                          ? "border-purple-200"
                          : "border-stone-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="text-base text-primary-500" icon="fluent-color:agents-48" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{formatName(memberId)}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <span>{member.type}</span>
                          {model && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span>{model}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* System Message */}
                    {systemMessage && (
                      <div className="mt-1.5 ml-6">
                        <div className="flex items-start gap-1 text-xs">
                          <Icon
                            className="text-sm text-green-500 mt-0.5 flex-shrink-0"
                            icon="mdi:message-text"
                          />
                          <span
                            className="text-gray-600 dark:text-gray-400 italic"
                            title={systemMessage}
                          >
                            {truncateText(systemMessage, TRUNCATE_LENGTH_LONG)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Tools - compact */}
                    {member.tools && member.tools.length > 0 && (
                      <div className="mt-1.5 ml-6 flex flex-wrap gap-1">
                        {member.tools.map((tool) => (
                          <span
                            key={tool}
                            className="text-xs text-gray-500 bg-gray-100 dark:bg-default-100 rounded px-1 py-0.5 flex items-center gap-0.5"
                          >
                            <Icon className="text-xs" icon="qlementine-icons:tool-16" />
                            {formatName(tool).slice(0, 15)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Fallback: show simple member list
          <div className="text-sm text-gray-600">
            <div className="font-medium mb-1">Members:</div>
            {data.members.map((member) => (
              <div key={member} className="text-xs text-gray-500 pl-2">
                {formatName(member)}
              </div>
            ))}
          </div>
        )}
      </div>

      <Handle className={`w-16 ${config.handleColor}`} position={Position.Top} type="target" />
      <Handle className={`w-16 ${config.handleColor}`} position={Position.Bottom} type="source" />
    </div>
  );
}

export default memo(TeamNode);
